import moment from 'moment';
import { connect } from 'react-redux';
import { Link } from '../../lib/routerAdapter';
import React, { type ReactNode, useEffect, useState } from 'react';
import { Dropdown, notification } from 'antd';
import styled from 'styled-components';

import type { UserAccess } from '../../lib/types';
import { notifyApi } from '../../lib/api';
import {
  bioTreatmentFacilityFactStatusEnum,
  accessTypeEnum,
  notificationTypeEnum,
} from '../../lib/enum';

type NotifyKey = 'bioTreatmentFacilityFact' | 'expiringCoupons';

type NotifyStateEntry = {
  skip: string | null;
  timerId: number | null;
  delay: number;
  callback: () => Promise<ReactNode | string | null>;
  canShow: (userAccess: UserAccess[]) => boolean;
};

type NotifyState = Record<NotifyKey, NotifyStateEntry>;

type NotifyProps = {
  children?: ReactNode;
  userAccess: UserAccess[];
};

const getErrorMessage = (error: unknown): string =>
  error instanceof Error ? error.message : String(error);

const INIT_STATE: NotifyState = {
  bioTreatmentFacilityFact: {
    skip: null,
    timerId: null,
    delay: 600000,
    callback: async () => {
      try {
        await notifyApi.notification(
          notificationTypeEnum.bioTreatmentFacilityFact
        );
      } catch (e: unknown) {
        const message = getErrorMessage(e);
        if (message !== 'Unexpected end of JSON input') {
          if (
            message.indexOf(
              'Имеются контаргенты у которых присутствуют не привязанные талоны.'
            ) + 1
          ) {
            return (
              <>
                Журнал въездов: {message}{' '}
                <Link to="/bioTreatmentFacilityFact?status=notPaid&status=notFound">
                  перейти
                </Link>
              </>
            );
          }
          return `Журнал въездов: ${message}`;
        }
      }
      return null;
    },
    canShow: (userAccess) =>
      userAccess.some((access) =>
        [accessTypeEnum.notifBio, accessTypeEnum.admin].includes(access)
      ),
  },
  expiringCoupons: {
    skip: null,
    timerId: null,
    delay: 500000,
    callback: async () => {
      try {
        await notifyApi.notification(notificationTypeEnum.expiringCoupons);
      } catch (e: unknown) {
        const message = getErrorMessage(e);
        if (message !== 'Unexpected end of JSON input') {
          return message;
        }
      }
      return null;
    },
    canShow: (userAccess) =>
      userAccess.some((access) =>
        [
          accessTypeEnum.expiringCouponsNotification,
          accessTypeEnum.admin,
        ].includes(access)
      ),
  },
};

const NotifyContent = ({ children, userAccess }: NotifyProps) => {
  const [state, setState] = useState<NotifyState | null>(null);
  const [timerId, setTimerId] = useState<number | null>(null);
  const dontShow =
    (field: NotifyKey) =>
    ({ key }: { key: string }) => {
      handleState(field, {
        skip: key,
      });
      notification.destroy(field);
    };

  const handleState = (
    key: NotifyKey,
    value: Partial<NotifyStateEntry>
  ) => {
    setState((oldState) => {
      if (!oldState) return oldState;

      const newState = {
        ...oldState,
        [key]: {
          ...oldState[key],
          ...value,
        },
      };
      window.localStorage.setItem(
        'notify',
        JSON.stringify({
          ...newState,
          [key]: {
            ...newState[key],
            timerId: null,
          },
        })
      );
      return newState;
    });
  };

  const showNotif = async (key: NotifyKey) => {
    const desc = await INIT_STATE[key].callback();
    if (desc) {
      notification.warning({
        key,
        duration: 0,
        description: <Description text={desc} dontShow={dontShow(key)} />,
      });
    }
  };

  const handleNotif = (key: NotifyKey, skip: string) => {
    showNotif(key);
    handleState(key, { skip });
  };

  const checkNotif = () => {
    setTimerId(
      setInterval(() => {
        state &&
          (Object.keys(state) as NotifyKey[]).forEach((key) => {
            if (!INIT_STATE[key].canShow(userAccess)) {
              return;
            }
            const skip = moment()
              .add(INIT_STATE[key].delay, 'milliseconds')
              .toISOString();
            if (state[key].skip === null) {
              handleNotif(key, skip);
            } else {
              let ms = moment().diff(moment(state[key].skip));
              if (ms > 0) {
                handleNotif(key, skip);
              }
            }
          });
      }, 5000)
    );
  };

  useEffect(() => {
    const state = window.localStorage.getItem('notify');
    if (state) {
      setState(JSON.parse(state) as NotifyState);
    } else {
      setState(INIT_STATE);
    }
  }, []);
  useEffect(() => {
    if (timerId !== null) clearInterval(timerId);
    checkNotif();
  }, [state, userAccess]);

  return <>{children}</>;
};

export default connect((state: any) => ({
  userAccess: state.auth.profile.access,
}))(NotifyContent);

const StyledDiv = styled('div')`
  text-align: right;
  padding: 5px 20px 0px 20px;
`;
const StyledSpan = styled('span')`
  font-size: 12px;
  text-decoration: underline;
  color: #1e93ff;
  cursor: pointer;
`;

type DescriptionProps = {
  text: ReactNode;
  dontShow: ({ key }: { key: string }) => void;
};

const Description = (props: DescriptionProps) => {
  return (
    <div>
      <div>{props.text}</div>
      <StyledDiv>
        <Dropdown
          menu={{
            onClick: info => props.dontShow({ key: String(info.key) }),
            items: [
              {
                key: moment().add(1, 'hour').toISOString(),
                label: '1 час',
              },
              {
                key: moment().add(4, 'hour').toISOString(),
                label: '4 часа',
              },
              {
                key: moment().endOf('day').toISOString(),
                label: 'сегодня',
              },
            ],
          }}
        >
          <StyledSpan>не показывать</StyledSpan>
        </Dropdown>
      </StyledDiv>
    </div>
  );
};
